<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { PropType } from 'vue'
    import type { DiscreteScaleLevel } from '@Scales/Model'
    import type { CriterionScaleEditorViewModel } from "@Criteria/Hierarchies/Components/CriterionScaleEditor"

    // ---------------- props / emits ----------------

    const props = defineProps({
        viewModel: {
            type: Object as PropType<CriterionScaleEditorViewModel>,
            required: true
        }
    })

    const emit = defineEmits<{
        (e: 'assign-custom-scale', criterionId: number, levels: DiscreteScaleLevel[]): void
    }>()

    // ---------------- state ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value.isOpen)

    const criterionId = computed(() => vm.value.criterionId)
    const criterionName = computed(() => vm.value.criterionName)
    const criterionDescription = computed(() => vm.value.criterionDescription)
    const error = computed(() => vm.value.error)

    function clearValidationErrors()
    {
        vm.value.error = null
    }

    function addLevel()
    {
        clearValidationErrors()
        vm.value.levels.push({ Name: null, Value: null, Definition: null })
    }

    function deleteLevel(l: DiscreteScaleLevel)
    {
        clearValidationErrors()
        vm.value.levels.remove(l)
    }

    function emitAssignCustomScale()
    {
        clearValidationErrors()
        emit("assign-custom-scale", criterionId.value, vm.value.levels)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Criterion: {{criterionName}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="criterion-scale-editor-form" @submit.prevent="emitAssignCustomScale">
                            <p class="lead">{{criterionDescription}}</p>

                            <div class="row" style="margin-bottom: 5px;">
                                <div class="col-sm-3">
                                    <label>Scale level <span class="text-danger">*</span></label>
                                </div>
                                <div class="col-sm-2">
                                    <label>Value <span class="text-danger">*</span></label>
                                </div>
                                <div class="col-sm-6">
                                    <label>Definition</label>
                                </div>
                            </div>

                            <div>
                                <div v-for="sl in vm.levels" :key="sl" class="row" style="margin-bottom: 5px;">
                                    <div class="col-sm-3">
                                        <input v-model.trim="sl.Name" type="text" required maxlength="100" class="form-control" placeholder="Level">
                                    </div>
                                    <div class="col-sm-2">
                                        <input type="number" v-model.number="sl.Value" placeholder="Number..." class="form-control" required min="0" step="any">
                                    </div>
                                    <div class="col-sm-6">
                                        <input v-model.trim="sl.Definition" type="text" class="form-control" placeholder="Definition for level">
                                    </div>
                                    <div class="col-sm-1">
                                        <a @click="deleteLevel(sl)" class="pointer fa fa-times"></a>
                                    </div>
                                </div>
                            </div>
                            <div class="error">{{error}}</div>

                            <p><a class="text-primary" @click="addLevel">Add scale level</a></p>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="criterion-scale-editor-form" class="btn btn-primary">Update</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
